import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import {
  ChevronDown, ChevronRight, Edit2, Save, X, Plus, Trash2,
  RefreshCw, CheckCircle2, AlertTriangle, Info,
} from 'lucide-react';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';
import { sectionType } from '../lib/sectionType';

/* ─── Constants ─────────────────────────────────────── */
// Phase 7: rules editor groups now bucket sections by TYPE rather
// than by a hardcoded list of legacy v1 keys. Each group declares
// the type(s) it accepts; ``sectionType()`` resolves wizard sections
// via their explicit ``type`` field and legacy sections via the
// key-pattern fallback. Wizard clients with arbitrary section IDs
// (``fringe_1``, ``overhead_my_company_oh``, ``allocation_facility``,
// ``ga_main``) categorize correctly without anyone updating the
// ``keys`` arrays.
const SCHEDULE_GROUPS = [
  {
    id: 'fringe',
    label: 'Fringe',
    types: ['fringe'],
  },
  {
    id: 'overhead',
    label: 'Overhead (OH)',
    types: ['overhead'],
  },
  {
    id: 'allocations',
    label: 'Allocations',
    types: ['allocation'],
  },
  {
    id: 'other',
    label: 'G&A / M&H / Schedule E',
    types: ['ga', 'material_handling', 'schedule_e', 'schedule'],
  },
  {
    id: 'schedule_h',
    label: 'Schedule H',
    types: [],
    readonly: true,
  },
];

const STEP_TYPE_COLORS = {
  compute_pool: 'bg-blue-100 text-blue-800',
  compute_base: 'bg-green-100 text-green-800',
  apply_rate: 'bg-blue-100 text-blue-800',
  calculate_rate: 'bg-amber-100 text-amber-800',
  add_allocation: 'bg-teal-100 text-teal-800',
  define_objectives: 'bg-slate-100 text-slate-700',
  default: 'bg-gray-100 text-gray-700',
};

const OPERATORS = ['equals', 'not_equals', 'in', 'not_in', 'begins_with', 'not_starts_with', 'contains', 'greater_than', 'less_than'];
const GL_FIELDS = [
  'Account ID Level 1',
  'Account ID Level 2',
  'Organizational ID Level 4',
  'Organizational ID Level 5',
  'Company ID',
  'Project Type Desc',
];

/* ─── Helpers ────────────────────────────────────────── */
function typeBadge(type) {
  const cls = STEP_TYPE_COLORS[type] || STEP_TYPE_COLORS.default;
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{type}</span>;
}

function conditionText(cond) {
  if (!cond) return '';
  const vals = cond.values ? cond.values.join(', ') : cond.value;
  return `${cond.field} ${cond.operator} ${vals}`;
}

/* ─── Condition editor ───────────────────────────────── */
function ConditionRow({ cond, onChange, onDelete }) {
  const isMulti = cond.operator === 'in' || cond.operator === 'not_in';
  // Local string state for multi-value input so commas/spaces aren't eaten mid-type
  const [multiRaw, setMultiRaw] = React.useState((cond.values || []).join(', '));
  // Sync if cond.values changes externally (e.g. operator switch)
  const prevValues = React.useRef(cond.values);
  React.useEffect(() => {
    if (JSON.stringify(cond.values) !== JSON.stringify(prevValues.current)) {
      setMultiRaw((cond.values || []).join(', '));
      prevValues.current = cond.values;
    }
  }, [cond.values]);

  return (
    <div className="flex gap-2 items-center mb-1">
      <select
        className="border rounded px-2 py-1 text-sm flex-1"
        value={cond.field}
        onChange={e => onChange({ ...cond, field: e.target.value })}
      >
        {GL_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
        {!GL_FIELDS.includes(cond.field) && <option value={cond.field}>{cond.field}</option>}
      </select>
      <select
        className="border rounded px-2 py-1 text-sm w-36"
        value={cond.operator}
        onChange={e => {
          const op = e.target.value;
          const multi = op === 'in' || op === 'not_in';
          onChange({
            ...cond,
            operator: op,
            ...(multi ? { values: cond.values || (cond.value ? [cond.value] : []), value: undefined } : { value: cond.value || (cond.values || []).join(','), values: undefined }),
          });
        }}
      >
        {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {isMulti ? (
        <Input
          className="text-sm flex-1"
          placeholder="comma-separated values"
          value={multiRaw}
          onChange={e => setMultiRaw(e.target.value)}
          onBlur={() => {
            const parsed = multiRaw.split(',').map(v => v.trim()).filter(Boolean);
            prevValues.current = parsed;
            onChange({ ...cond, values: parsed });
          }}
        />
      ) : (
        <Input
          className="text-sm flex-1"
          placeholder="value"
          value={cond.value || ''}
          onChange={e => onChange({ ...cond, value: e.target.value })}
        />
      )}
      <button onClick={onDelete} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
    </div>
  );
}

/* ─── Rule editor modal ──────────────────────────────── */
function RuleEditor({ rule, onSave, onCancel }) {
  const [local, setLocal] = useState(JSON.parse(JSON.stringify(rule)));

  const updateCond = (i, updated) => {
    const conds = [...(local.conditions || [])];
    conds[i] = updated;
    setLocal({ ...local, conditions: conds });
  };

  const deleteCond = (i) => {
    const conds = [...(local.conditions || [])];
    conds.splice(i, 1);
    setLocal({ ...local, conditions: conds });
  };

  const addCond = () => {
    const conds = [...(local.conditions || [])];
    conds.push({ field: 'Account ID Level 1', operator: 'equals', value: '' });
    setLocal({ ...local, conditions: conds });
  };

  return (
    <div className="border rounded-lg p-4 bg-slate-50 mt-2 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-sm font-medium text-slate-600 w-16">Type</div>
        <select
          className="border rounded px-2 py-1 text-sm"
          value={local.type}
          onChange={e => setLocal({ ...local, type: e.target.value })}
        >
          {Object.keys(STEP_TYPE_COLORS).filter(k => k !== 'default').map(t =>
            <option key={t} value={t}>{t}</option>
          )}
        </select>

        {local.type === 'apply_rate' && (
          <>
            <span className="text-sm text-slate-500">rate_reference:</span>
            <Input className="text-sm w-40" value={local.rate_reference || ''} onChange={e => setLocal({ ...local, rate_reference: e.target.value })} />
            <span className="text-sm text-slate-500">target:</span>
            <select className="border rounded px-2 py-1 text-sm" value={local.target || 'pool'} onChange={e => setLocal({ ...local, target: e.target.value })}>
              <option value="pool">pool</option>
              <option value="base">base</option>
            </select>
          </>
        )}

        {local.type === 'add_allocation' && (
          <>
            <span className="text-sm text-slate-500">allocation_source:</span>
            <Input className="text-sm w-48" value={local.allocation_source || ''} onChange={e => setLocal({ ...local, allocation_source: e.target.value })} />
          </>
        )}
      </div>

      {(local.conditions !== undefined || ['compute_pool', 'compute_base', 'apply_rate'].includes(local.type)) && (
        <div>
          <div className="text-sm font-medium text-slate-600 mb-1">Conditions</div>
          {(local.conditions || []).map((c, i) => (
            <ConditionRow key={i} cond={c} onChange={u => updateCond(i, u)} onDelete={() => deleteCond(i)} />
          ))}
          <button onClick={addCond} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 mt-1">
            <Plus className="h-3 w-3" /> Add condition
          </button>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={() => onSave(local)}>
          <Save className="h-4 w-4 mr-1" /> Save Rule
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

/* ─── Section panel ──────────────────────────────────── */
function SectionPanel({ section, token, clientId, readonly }) {
  const [expanded, setExpanded] = useState(false);
  const [rules, setRules] = useState(section.rules || []);
  const [editingIdx, setEditingIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(clientId ? { 'X-Client-Id': clientId } : {}),
  };

  const persist = async (updatedRules) => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`${API}/gl-calcs/rules/sections/${section.key}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ rules: updatedRules }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setSaveMsg({ ok: true, text: `Saved — ${data.rules_count} rules` });
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRule = (idx, updated) => {
    const next = [...rules];
    next[idx] = { ...updated, step: idx + 1 };
    setRules(next);
    setEditingIdx(null);
    persist(next);
  };

  const handleDelete = (idx) => {
    const next = rules.filter((_, i) => i !== idx).map((r, i) => ({ ...r, step: i + 1 }));
    setRules(next);
    persist(next);
  };

  const handleAdd = () => {
    const next = [...rules, { step: rules.length + 1, type: 'compute_pool', conditions: [] }];
    setRules(next);
    setEditingIdx(next.length - 1);
  };

  return (
    <div className="border rounded-lg overflow-hidden mb-2">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          <span className="font-medium text-slate-800">{section.label}</span>
          <Badge variant="secondary" className="text-xs">{section.key}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {readonly && <Badge variant="outline" className="text-xs text-slate-500">read-only</Badge>}
          <span className="text-sm text-slate-500">{rules.length} rule{rules.length !== 1 ? 's' : ''}</span>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 bg-white border-t">
          {saveMsg && (
            <div className={`mt-2 mb-3 flex items-center gap-2 text-sm ${saveMsg.ok ? 'text-green-700' : 'text-red-700'}`}>
              {saveMsg.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {saveMsg.text}
            </div>
          )}

          {rules.length === 0 && (
            <div className="py-4 text-sm text-slate-400 text-center">No rules defined for this section.</div>
          )}

          {rules.map((rule, idx) => (
            <div key={idx} className="mt-3">
              <div className="flex items-start gap-3">
                <span className="text-xs text-slate-400 font-mono mt-1 w-6">#{rule.step}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {typeBadge(rule.type)}
                    {rule.rate_reference && (
                      <span className="text-xs text-slate-500">→ <strong>{rule.rate_reference}</strong> ({rule.target})</span>
                    )}
                    {rule.allocation_source && (
                      <span className="text-xs text-slate-500">← <strong>{rule.allocation_source}</strong></span>
                    )}
                  </div>
                  {(rule.conditions || []).length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {rule.conditions.map((c, ci) => (
                        <div key={ci} className="text-xs text-slate-600 font-mono bg-slate-50 rounded px-2 py-0.5">
                          {conditionText(c)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!readonly && editingIdx !== idx && (
                  <div className="flex gap-1 ml-auto">
                    <button onClick={() => setEditingIdx(idx)} className="text-slate-400 hover:text-blue-600 p-1">
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(idx)} className="text-slate-400 hover:text-red-600 p-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {editingIdx === idx && (
                <RuleEditor
                  rule={rule}
                  onSave={(updated) => handleSaveRule(idx, updated)}
                  onCancel={() => setEditingIdx(null)}
                />
              )}
            </div>
          ))}

          {!readonly && (
            <div className="mt-4 flex items-center gap-3">
              <Button size="sm" variant="outline" onClick={handleAdd} disabled={saving}>
                <Plus className="h-4 w-4 mr-1" /> Add Rule
              </Button>
              {saving && <span className="text-sm text-slate-500 flex items-center gap-1"><RefreshCw className="h-3 w-3 animate-spin" /> Saving…</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Schedule H filter fields ───────────────────────── */
const SCHED_H_FILTER_FIELDS = [
  'account.level_1', 'account.level_2', 'account.level_3',
  'org.level_4', 'org.level_5',
  'Project Type', 'Project ID',
];

/* ─── Schedule H rule row editor ─────────────────────── */
function SchedHFilterRow({ f, onChange, onDelete }) {
  const isMulti = f.operator === 'in' || f.operator === 'not_in';
  const [multiRaw, setMultiRaw] = React.useState(
    Array.isArray(f.value) ? f.value.join(', ') : (f.value || '')
  );
  return (
    <div className="flex gap-2 items-center mb-1">
      <select
        className="border rounded px-2 py-1 text-sm flex-1"
        value={f.field || ''}
        onChange={e => onChange({ ...f, field: e.target.value })}
      >
        {SCHED_H_FILTER_FIELDS.map(fld => <option key={fld} value={fld}>{fld}</option>)}
        {!SCHED_H_FILTER_FIELDS.includes(f.field) && f.field && (
          <option value={f.field}>{f.field}</option>
        )}
      </select>
      <select
        className="border rounded px-2 py-1 text-sm w-36"
        value={f.operator || 'equals'}
        onChange={e => {
          const op = e.target.value;
          const multi = op === 'in' || op === 'not_in';
          const curVal = Array.isArray(f.value) ? f.value.join(', ') : (f.value || '');
          onChange({
            ...f, operator: op,
            value: multi
              ? (Array.isArray(f.value) ? f.value : curVal.split(',').map(v => v.trim()).filter(Boolean))
              : (Array.isArray(f.value) ? f.value.join(', ') : (f.value || '')),
          });
        }}
      >
        {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {isMulti ? (
        <Input
          className="text-sm flex-1"
          placeholder="comma-separated"
          value={multiRaw}
          onChange={e => setMultiRaw(e.target.value)}
          onBlur={() => onChange({ ...f, value: multiRaw.split(',').map(v => v.trim()).filter(Boolean) })}
        />
      ) : (
        <Input
          className="text-sm flex-1"
          placeholder="value"
          value={Array.isArray(f.value) ? f.value.join(', ') : (f.value || '')}
          onChange={e => onChange({ ...f, value: e.target.value })}
        />
      )}
      <button onClick={onDelete} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
    </div>
  );
}

function SchedHRuleEditor({ rule, onSave, onCancel }) {
  const [local, setLocal] = useState(JSON.parse(JSON.stringify(rule)));
  const [acctRaw, setAcctRaw] = useState((rule.sourceAccounts || []).join(', '));

  const isAggGL = local.ruleType === 'aggregate_gl';

  const updateFilter = (i, updated) => {
    const filters = [...(local.filters || [])];
    filters[i] = updated;
    setLocal({ ...local, filters });
  };
  const deleteFilter = (i) => {
    const filters = [...(local.filters || [])];
    filters.splice(i, 1);
    setLocal({ ...local, filters });
  };
  const addFilter = () => {
    setLocal({ ...local, filters: [...(local.filters || []), { field: 'account.level_1', operator: 'equals', value: '' }] });
  };

  const handleSave = () => {
    const saved = { ...local };
    if (isAggGL) {
      saved.sourceAccounts = acctRaw.split(',').map(v => v.trim()).filter(Boolean);
    }
    onSave(saved);
  };

  return (
    <div className="border rounded-lg p-4 bg-slate-50 mt-2 space-y-3">
      <div className="flex items-center gap-3 text-sm">
        <span className="text-slate-500 font-medium">Column:</span>
        <Input
          className="text-sm max-w-xs"
          value={local.displayColumnLabel || ''}
          onChange={e => setLocal({ ...local, displayColumnLabel: e.target.value })}
        />
        <Badge className="text-xs">{local.ruleType}</Badge>
      </div>

      {isAggGL && (
        <div>
          <div className="text-sm font-medium text-slate-600 mb-1">
            Source Accounts <span className="text-slate-400 font-normal">(comma-separated; leave empty to match by filters only)</span>
          </div>
          <Input
            className="text-sm font-mono"
            placeholder="e.g. 82-20-000, 82-30-000, 82-90-900"
            value={acctRaw}
            onChange={e => setAcctRaw(e.target.value)}
          />
        </div>
      )}

      {isAggGL && (
        <div>
          <div className="text-sm font-medium text-slate-600 mb-1">Filters</div>
          {(local.filters || []).map((f, i) => (
            <SchedHFilterRow key={i} f={f} onChange={u => updateFilter(i, u)} onDelete={() => deleteFilter(i)} />
          ))}
          <button onClick={addFilter} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 mt-1">
            <Plus className="h-3 w-3" /> Add filter
          </button>
        </div>
      )}

      {local.ruleType === 'expression' && (
        <div>
          <div className="text-sm font-medium text-slate-600 mb-1">Expression</div>
          <Input
            className="text-sm font-mono"
            value={local.expression || ''}
            onChange={e => setLocal({ ...local, expression: e.target.value })}
          />
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={handleSave}><Save className="h-4 w-4 mr-1" />Save Rule</Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

/* ─── Schedule H panel (editable) ───────────────────── */
function ScheduleHPanel({ token, clientId }) {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [search, setSearch] = useState('');
  const [editingRowId, setEditingRowId] = useState(null);
  const [expandedCols, setExpandedCols] = useState(new Set());

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(clientId ? { 'X-Client-Id': clientId } : {}),
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/compiled-onboarding/${clientId}/schedule-h-rules`, { headers });
      if (!res.ok) throw new Error('Failed to fetch Schedule H rules');
      const data = await res.json();
      setRules(data.rules || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);  // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const persist = async (updatedRules) => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`${API}/compiled-onboarding/${clientId}/schedule-h-rules`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ rules: updatedRules }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setSaveMsg({ ok: true, text: `Saved — ${data.ruleCount} rules. Re-run Schedule H to see updated results.` });
      setTimeout(() => setSaveMsg(null), 5000);
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRule = (rowId, updated) => {
    const next = rules.map(r => r.rowId === rowId ? updated : r);
    setRules(next);
    setEditingRowId(null);
    persist(next);
  };

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm"><RefreshCw className="h-4 w-4 animate-spin inline mr-2" />Loading Schedule H rules…</div>;
  if (error) return <Alert className="border-red-200 bg-red-50"><AlertDescription className="text-red-700">{error}</AlertDescription></Alert>;
  if (!rules) return null;

  // Group rules by displayColumnLabel
  const colMap = {};
  for (const r of rules) {
    const col = r.displayColumnLabel || r.label || r.rowId || '(unlabelled)';
    if (!colMap[col]) colMap[col] = [];
    colMap[col].push(r);
  }
  const colEntries = Object.entries(colMap).filter(([col]) =>
    !search || col.toLowerCase().includes(search.toLowerCase()) ||
    colMap[col].some(r => JSON.stringify(r).toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div>
      {saveMsg && (
        <div className={`mb-3 flex items-center gap-2 text-sm p-3 rounded-lg ${saveMsg.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {saveMsg.ok ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 flex-shrink-0" />}
          {saveMsg.text}
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <Input
          placeholder="Search columns or rules…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <span className="text-sm text-slate-500">{rules.length} rules across {Object.keys(colMap).length} columns</span>
        {saving && <span className="text-sm text-slate-500 flex items-center gap-1"><RefreshCw className="h-3 w-3 animate-spin" />Saving…</span>}
      </div>

      {colEntries.map(([col, colRules]) => {
        const isExpanded = expandedCols.has(col);
        return (
          <div key={col} className="border rounded-lg mb-2 overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              onClick={() => {
                const next = new Set(expandedCols);
                isExpanded ? next.delete(col) : next.add(col);
                setExpandedCols(next);
              }}
            >
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                <span className="font-medium text-slate-700">{col}</span>
              </div>
              <span className="text-sm text-slate-500">{colRules.length} rule{colRules.length !== 1 ? 's' : ''}</span>
            </button>

            {isExpanded && (
              <div className="divide-y bg-white px-4 pb-2">
                {colRules.map((rule) => (
                  <div key={rule.rowId} className="py-3">
                    <div className="flex items-start gap-3">
                      <span className="text-xs text-slate-400 font-mono mt-1 w-16 flex-shrink-0">{rule.rowId}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {typeBadge(rule.ruleType)}
                          {(rule.sourceAccounts || []).length > 0 && (
                            <span className="text-xs text-slate-500 font-mono">
                              accounts: {rule.sourceAccounts.slice(0, 4).join(', ')}{rule.sourceAccounts.length > 4 ? ` +${rule.sourceAccounts.length - 4}` : ''}
                            </span>
                          )}
                        </div>
                        {(rule.filters || []).length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {rule.filters.map((f, fi) => (
                              <div key={fi} className="text-xs text-slate-600 font-mono bg-slate-50 rounded px-2 py-0.5">
                                {f.field} {f.operator} {Array.isArray(f.value) ? f.value.join(', ') : f.value}
                              </div>
                            ))}
                          </div>
                        )}
                        {rule.expression && (
                          <div className="text-xs text-slate-500 font-mono mt-1">= {rule.expression}</div>
                        )}
                      </div>
                      {editingRowId !== rule.rowId && (
                        <button
                          onClick={() => setEditingRowId(rule.rowId)}
                          className="text-slate-400 hover:text-blue-600 p-1 flex-shrink-0"
                          title="Edit rule"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {editingRowId === rule.rowId && (
                      <SchedHRuleEditor
                        rule={rule}
                        onSave={(updated) => handleSaveRule(rule.rowId, updated)}
                        onCancel={() => setEditingRowId(null)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


/* ─── Main component ─────────────────────────────────── */
const RulesEditor = ({ token }) => {
  const { clientId, loading: clientLoading } = useClient();
  const [activeGroup, setActiveGroup] = useState('fringe');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(clientId ? { 'X-Client-Id': clientId } : {}),
  };

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/gl-calcs/rules/latest`, { headers });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to load rules');
      }
      const data = await res.json();
      const raw = (data.structured_rules?.sections || []);
      setSections(raw);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);  // eslint-disable-line

  useEffect(() => {
    if (!clientLoading && clientId) loadRules();
  }, [loadRules, clientLoading, clientId]);

  // Phase 7: bucket sections into groups by section type. Each
  // group's ``types`` set decides which sections land there;
  // ``sectionType()`` resolves wizard sections via their explicit
  // ``type`` and legacy sections via the key-pattern fallback.
  const groups = SCHEDULE_GROUPS.map(g => ({
    ...g,
    sections: sections.filter(s => (g.types || []).includes(sectionType(s))),
  }));

  // Sections whose type doesn't match any named group (or that
  // don't have a recognizable type at all) fall into the "Other"
  // bucket so they're still editable.
  const knownTypes = new Set(SCHEDULE_GROUPS.flatMap(g => g.types || []));
  const ungrouped = sections.filter(s => !knownTypes.has(sectionType(s)));
  if (ungrouped.length > 0) {
    groups.push({ id: 'ungrouped', label: 'Other', sections: ungrouped, types: [], readonly: false });
  }

  const activeGroupData = groups.find(g => g.id === activeGroup) || groups[0];

  if (clientLoading || !clientId) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-400 text-sm">
        <RefreshCw className="h-4 w-4 animate-spin" />
        {clientLoading ? 'Loading client…' : 'No client selected. Please select a client to view rules.'}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rules Editor</h1>
          <p className="text-slate-500 text-sm mt-1">View and edit calculation rules by schedule</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadRules} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {!loading && sections.length === 0 && !error && (
        <Alert className="border-amber-200 bg-amber-50">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 ml-2">No rules found for this client. Upload a rules file to get started.</AlertDescription>
        </Alert>
      )}

      {/* Group tabs */}
      <div className="flex gap-2 flex-wrap">
        {groups.map(g => (
          <button
            key={g.id}
            onClick={() => setActiveGroup(g.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeGroup === g.id
                ? 'bg-slate-900 text-white'
                : 'bg-white border text-slate-700 hover:bg-slate-50'
            }`}
          >
            {g.label}
            {g.id !== 'schedule_h' && (
              <span className={`ml-2 text-xs rounded-full px-1.5 py-0.5 ${activeGroup === g.id ? 'bg-white/20' : 'bg-slate-100'}`}>
                {g.sections?.length || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Section list */}
      <div>
        {activeGroupData?.id === 'schedule_h' ? (
          <ScheduleHPanel token={token} clientId={clientId} />
        ) : (
          activeGroupData?.sections?.length > 0 ? (
            activeGroupData.sections.map(s => (
              <SectionPanel
                key={s.key}
                section={s}
                token={token}
                clientId={clientId}
                readonly={activeGroupData.readonly || false}
              />
            ))
          ) : (
            <div className="py-8 text-center text-slate-400 text-sm">
              No sections in this group.
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default RulesEditor;
